import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable, Subject } from 'rxjs';

export interface IWebSocketMessageGeneric {
  [key: string]: any;
}

export interface IWebSocketMessageData extends IWebSocketMessageGeneric {
  action: string;
}

export interface IWebSocketMessage<T> {
  topic: string;
  data: T & IWebSocketMessageData;
}

export enum EWebSocketState {
  CONNECTING,
  OPEN,
  CLOSED,
  NOT_AVAILABLE,
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private webSocket: WebSocket;
  private readonly eventListeners: Map<string, Subject<any>> = new Map<
    string,
    Subject<any>
  >();

  private readonly PONG_INTERVAL: number = 30000; // ms
  private readonly RECONNECT_INTERVAL: number = 5000; // ms
  private readonly MAX_RECONNECTION_ATTEMPTS: number = 5;

  private pingSent: boolean;
  private pingInterval: undefined | ReturnType<typeof setTimeout>;
  private reconnectionAttempts = 0;

  private accessToken: string;

  private state: EWebSocketState = EWebSocketState.NOT_AVAILABLE;

  private pendingSends: string[] = [];

  constructor() {
  }

  get connected() {
    return this.state === EWebSocketState.OPEN;
  }

  open(accessToken: string, reconnect: boolean = false) {
    if (this.state === EWebSocketState.CONNECTING && !reconnect) {
      return;
    }
    this.state = EWebSocketState.CONNECTING;

    if (
      reconnect &&
      this.reconnectionAttempts >= this.MAX_RECONNECTION_ATTEMPTS
    ) {
      console.log('Max reconnection attempts reached');
      this.state = EWebSocketState.NOT_AVAILABLE;
      return;
    }
    this.webSocket = new WebSocket(`${environment.wsUrl}?token=${accessToken}`);

    this.webSocket.addEventListener('open', this.onSocketOpen.bind(this));
    this.webSocket.addEventListener('close', this.onSocketClose.bind(this));
    this.webSocket.addEventListener('error', this.onSocketError.bind(this));
    this.webSocket.addEventListener('message', this.onSocketMessage.bind(this));

    if (!reconnect) {
      this.accessToken = accessToken;
    }
  }

  on<T>(topic: string): Observable<T & IWebSocketMessageData> {
    const eventListener = this.eventListeners.get(topic);

    if (!eventListener) {
      this.eventListeners.set(topic, new Subject<T>());
    }

    return this.eventListeners.get(topic);
  }

  close(reconnect: boolean = false) {
    console.log('Closing WS connection');
    this.webSocket.removeAllListeners('open');
    this.webSocket.removeAllListeners('close');
    this.webSocket.removeAllListeners('error');
    this.webSocket.removeAllListeners('message');
    this.webSocket.close();
    this.state = EWebSocketState.CLOSED;

    if (reconnect && this.accessToken) {
      this.open(this.accessToken, true);
    }
  }

  public send<T>(data: IWebSocketMessage<T>) {
    if (this.state !== EWebSocketState.OPEN) {
      console.log('Socket is not open, adding to pending sends');
      this.pendingSends.push(JSON.stringify(data));
      return;
    }
    this._send(JSON.stringify(data));
  }

  private sendPending() {
    if (this.state !== EWebSocketState.OPEN) {
      return;
    }
    this.pendingSends.forEach(message => {
      this._send(message);
    });
    this.pendingSends = [];
  }

  private onSocketOpen() {
    this.state = EWebSocketState.OPEN;
    this.pingInterval = setInterval(
      this.sentPing.bind(this),
      this.PONG_INTERVAL,
    );
    if (this.pendingSends.length) {
      setTimeout(this.sendPending.bind(this));
    }
  }

  private onSocketClose() {
    this.clearPingInterval();
    // complete all subscriptions
    this.eventListeners.forEach((eventListener: Subject<any>) => {
      eventListener.complete();
    });

    this.state = EWebSocketState.CLOSED;
  }

  private onSocketError(event: Event) {
    this.clearPingInterval();
    // complete all subscriptions
    this.eventListeners.forEach((eventListener: Subject<any>) => {
      eventListener.error(event);
      eventListener.complete();
    });
    this.state = EWebSocketState.NOT_AVAILABLE;

    if (this.reconnectionAttempts < this.MAX_RECONNECTION_ATTEMPTS) {
      // reconnect on error
      this.reconnectionAttempts++;
      setTimeout(() => {
        this.close(true);
      }, this.RECONNECT_INTERVAL);
    }
  }

  private onSocketMessage(event: MessageEvent<string>) {
    /**
     * All event message needs to follow the following structure:
     * {
     *     "topic": "topic_name",
     *     "data": {
     *         "key": "value"
     *     }
     * }
     *
     * There is a special case where the message equals to "pong" which means that the server response to ping request.
     */

    const message = event.data;

    if (message === 'pong') {
      this.pingSent = false;
      return;
    }

    const data: IWebSocketMessage<any> = JSON.parse(message);

    if (data && data.topic) {
      const eventListener = this.eventListeners.get(data.topic);

      if (eventListener) {
        eventListener.next(data.data);
      }
    }
  }

  private _send(message: string) {
    if (this.state !== EWebSocketState.OPEN) {
      return;
    }

    this.webSocket.send(message);
  }

  private sentPing() {
    if (this.pingSent) {
      return;
    }
    this.send({
      topic: 'keepAlive',
      data: {
        action: 'ping',
      },
    });

    this.pingSent = true;
  }

  private clearPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
  }
}
